<template>
  <popup-modal ref="popup" class="session-warning-modal">
    <div class="session-warning-content">
      <h2 class="warning-title">Session Expiring Soon</h2>
      <p class="warning-message">
        Your session will expire in <strong class="countdown-text">{{ formattedTime }}</strong> due to inactivity.
      </p>
      <p class="warning-submessage">
        Click "Continue" to stay logged in, or you will be automatically logged out.
      </p>
      <div class="countdown-container">
        <div class="countdown-circle" :class="{ 'warning': timeRemaining <= 60, 'critical': timeRemaining <= 30 }">
          <span class="countdown-number">{{ Math.ceil(timeRemaining) }}</span>
          <span class="countdown-label">seconds</span>
        </div>
      </div>
      <div class="modal-buttons">
        <button class="btn btn_primary continue-btn" @click="handleContinue">
          Continue Session
        </button>
      </div>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from "./PopupModal.vue";

export default {
  name: "SessionWarningModal",
  components: {
    PopupModal,
  },
  data() {
    return {
      timeRemaining: 0, // Time remaining in seconds
      countdownInterval: null,
      onContinue: null,
      onLogout: null,
    };
  },
  computed: {
    /**
     * Format time remaining as MM:SS
     */
    formattedTime() {
      const minutes = Math.floor(this.timeRemaining / 60);
      const seconds = Math.floor(this.timeRemaining % 60);
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    },
  },
  methods: {
    /**
     * Show the modal with countdown timer
     * @param {number} warningTimeSeconds - Time in seconds before session expires
     * @param {Function} onContinueCallback - Callback when user clicks Continue
     * @param {Function} onLogoutCallback - Callback when user clicks Logout or timer expires
     */
    show(warningTimeSeconds, onContinueCallback, onLogoutCallback) {
      this.timeRemaining = warningTimeSeconds;
      this.onContinue = onContinueCallback;
      this.onLogout = onLogoutCallback;
      this.$refs.popup.open();
      this.startCountdown();
    },

    /**
     * Update the remaining time (called from parent to sync with actual expiration)
     * @param {number} remainingSeconds - Remaining time in seconds
     */
    updateTimeRemaining(remainingSeconds) {
      this.timeRemaining = Math.max(0, remainingSeconds);
      
      // If time runs out, trigger logout
      if (this.timeRemaining <= 0) {
        this.handleLogout();
      }
    },

    /**
     * Close the modal programmatically (called from parent component)
     */
    close() {
      this.stopCountdown();
      this.$refs.popup.close();
    },

    /**
     * Start the countdown timer
     * Note: The countdown is now fully controlled by the parent component
     * which updates it via updateTimeRemaining() every second for perfect synchronization.
     * We don't need a local countdown interval since parent handles all updates.
     */
    startCountdown() {
      // Clear any existing interval (shouldn't be needed, but for safety)
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      
      // No local countdown needed - parent component updates via updateTimeRemaining()
      // This prevents double-counting and ensures perfect synchronization
    },

    /**
     * Stop the countdown timer
     */
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    /**
     * Handle Continue button click - extends session
     * Note: Modal will NOT close here - it will close only when session extends successfully
     * or user becomes unauthenticated (handled by parent component)
     */
    handleContinue() {
      // Don't close modal immediately - wait for session extension to complete
      if (this.onContinue) {
        this.onContinue();
      }
    },

    /**
     * Handle timer expiration - triggers logout
     */
    handleLogout() {
      this.stopCountdown();
      this.$refs.popup.close();
      if (this.onLogout) {
        this.onLogout();
      }
    },
  },
  beforeDestroy() {
    // Clean up interval on component destruction
    this.stopCountdown();
  },
};
</script>

<style scoped>
.session-warning-content {
  text-align: center;
  padding: 1rem;
}

.warning-title {
  color: #d9534f;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.warning-message {
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.countdown-text {
  color: #d9534f;
  font-size: 1.1rem;
  font-weight: 600;
}

.warning-submessage {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
}

.countdown-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}

.countdown-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid #5cb85c;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
}

.countdown-circle.warning {
  border-color: #f0ad4e;
  animation: pulse 1s infinite;
}

.countdown-circle.critical {
  border-color: #d9534f;
  animation: pulse 0.5s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.countdown-number {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.countdown-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  margin-top: 0.25rem;
}

.modal-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.continue-btn {
  min-width: 150px;
  padding: 10px 20px;
  font-weight: 600;
  background-color: #5cb85c;
  border-color: #5cb85c;
  color: white;
}

.continue-btn:hover {
  background-color: #4cae4c;
  border-color: #4cae4c;
}


/* Responsive design */
@media (max-width: 480px) {
  .modal-buttons {
    flex-direction: column;
  }

  .continue-btn {
    width: 100%;
  }

  .countdown-circle {
    width: 100px;
    height: 100px;
  }

  .countdown-number {
    font-size: 1.5rem;
  }
}
</style>

